import {
  CLEAR_SEF_TOAST,
  CLOSE_DELETE_CONFIRM,
  CLOSE_DRAWER,
  CLOSE_SETUP_PROMPT,
  CREATE_ENDPOINT_ASYNC,
  DELETE_ENDPOINT_ASYNC,
  DELETE_EVENT_RULE_ASYNC,
  FETCH_ENDPOINT_DETAIL_ASYNC,
  FETCH_ENDPOINTS_ASYNC,
  FETCH_EVENT_RULE_DETAIL_ASYNC,
  FETCH_EVENT_RULES_ASYNC,
  FETCH_SCOPE_AUTHORITY_ASYNC,
  OPEN_DELETE_CONFIRM,
  OPEN_DRAWER,
  OPEN_SETUP_PROMPT,
  SET_ACTIVE_TAB,
  SHOW_SEF_TOAST,
  STATUS,
  TABS,
  UPDATE_ENDPOINT_ASYNC,
} from './constants'

export const endpointsDraftedByRuleDelete = (ruleId, endpoints) =>
  endpoints.filter((ep) => {
    const ids = ep.connectedRuleIds || []
    return ep.status === STATUS.ACTIVE && ids.length === 1 && ids[0] === ruleId
  })

export const rulesDraftedByEndpointDelete = (endpointId, eventRules) =>
  eventRules.filter(
    (rule) =>
      rule.status !== STATUS.DRAFT &&
      rule.endpointIds?.length === 1 &&
      rule.endpointIds[0] === endpointId
  )

export const endpointsNarrowedByRule = (endpointIds, endpoints) =>
  endpoints.filter(
    (ep) =>
      endpointIds.includes(ep.id) && ep.status === STATUS.ACTIVE && !ep.connectedRuleIds?.length
  )

const closedDrawer = { open: false, type: null, mode: null, id: null, preselectEndpointIds: [] }

const syncEndpointRuleLinks = (endpoints, eventRules) =>
  endpoints.map((ep) => ({
    ...ep,
    connectedRuleIds: eventRules
      .filter((rule) => (rule.endpointIds || []).includes(ep.id))
      .map((rule) => rule.id),
  }))

const initialState = {
  activeTab: TABS.ENDPOINTS,
  endpoints: [],
  endpointsLoader: true,
  endpointsError: null,
  endpointDetail: null,
  endpointDetailLoader: false,
  endpointDetailError: null,
  endpointSaving: false,
  eventRules: [],
  eventRulesLoader: true,
  eventRulesError: null,
  ruleDetail: null,
  canEnableAllCompanies: null,
  drawer: closedDrawer,
  deleteConfirm: {
    open: false,
    entityType: null,
    id: null,
  },
  setupPrompt: {
    open: false,
    endpointId: null,
    name: '',
  },
  sefToast: null,
}

export default function alertsV2Reducer() {
  // eslint-disable-next-line default-param-last -- Redux reducer signature
  return (state = initialState, action) => {
    switch (action.type) {
      case SET_ACTIVE_TAB:
        return { ...state, activeTab: action.payload.tab }

      case OPEN_DRAWER:
        return {
          ...state,
          drawer: {
            open: true,
            type: action.payload.drawerType,
            mode: action.payload.mode,
            id: action.payload.id,
            preselectEndpointIds: action.payload.preselectEndpointIds || [],
          },
          endpointDetail: null,
          endpointDetailError: null,
          ruleDetail: null,
        }

      case CLOSE_DRAWER:
        return {
          ...state,
          drawer: closedDrawer,
          ruleDetail: null,
          endpointDetail: null,
          endpointDetailLoader: false,
          endpointDetailError: null,
        }

      case OPEN_DELETE_CONFIRM:
        return {
          ...state,
          deleteConfirm: {
            open: true,
            entityType: action.payload.entityType,
            id: action.payload.id,
          },
        }

      case CLOSE_DELETE_CONFIRM:
        return {
          ...state,
          deleteConfirm: { open: false, entityType: null, id: null },
        }

      case OPEN_SETUP_PROMPT:
        return {
          ...state,
          setupPrompt: {
            open: true,
            endpointId: action.payload.endpointId,
            name: action.payload.name,
          },
        }

      case CLOSE_SETUP_PROMPT:
        return {
          ...state,
          setupPrompt: { open: false, endpointId: null, name: '' },
        }

      case SHOW_SEF_TOAST:
        return { ...state, sefToast: action.payload }

      case CLEAR_SEF_TOAST:
        return { ...state, sefToast: null }

      case FETCH_ENDPOINTS_ASYNC.INITIATE:
        return { ...state, endpointsLoader: true, endpointsError: null }
      case FETCH_ENDPOINTS_ASYNC.SUCCESS:
        return {
          ...state,
          endpointsLoader: false,
          endpoints: syncEndpointRuleLinks(action.payload.data || [], state.eventRules),
        }
      case FETCH_ENDPOINTS_ASYNC.ERROR:
        return { ...state, endpointsLoader: false, endpointsError: action.error }

      case FETCH_ENDPOINT_DETAIL_ASYNC.INITIATE:
        return {
          ...state,
          endpointDetailLoader: true,
          endpointDetail: null,
          endpointDetailError: null,
        }
      case FETCH_ENDPOINT_DETAIL_ASYNC.SUCCESS:
        return {
          ...state,
          endpointDetailLoader: false,
          endpointDetail: action.payload.data,
        }
      case FETCH_ENDPOINT_DETAIL_ASYNC.ERROR:
        return { ...state, endpointDetailLoader: false, endpointDetailError: action.error }

      case CREATE_ENDPOINT_ASYNC.INITIATE:
      case UPDATE_ENDPOINT_ASYNC.INITIATE:
        return { ...state, endpointSaving: true }
      case CREATE_ENDPOINT_ASYNC.SUCCESS:
      case UPDATE_ENDPOINT_ASYNC.SUCCESS:
        return { ...state, endpointSaving: false }
      case CREATE_ENDPOINT_ASYNC.ERROR:
      case UPDATE_ENDPOINT_ASYNC.ERROR:
        return { ...state, endpointSaving: false }

      case DELETE_ENDPOINT_ASYNC.SUCCESS:
        return {
          ...state,
          deleteConfirm: { open: false, entityType: null, id: null },
        }

      case FETCH_EVENT_RULES_ASYNC.INITIATE:
        return { ...state, eventRulesLoader: true, eventRulesError: null }

      case FETCH_EVENT_RULES_ASYNC.SUCCESS: {
        const eventRules = action.payload.data || []
        return {
          ...state,
          eventRulesLoader: false,
          eventRules,
          endpoints: syncEndpointRuleLinks(state.endpoints, eventRules),
        }
      }

      case FETCH_EVENT_RULES_ASYNC.ERROR:
        return { ...state, eventRulesLoader: false, eventRulesError: action.error }

      case FETCH_SCOPE_AUTHORITY_ASYNC.INITIATE:
        return { ...state, canEnableAllCompanies: null }

      case FETCH_SCOPE_AUTHORITY_ASYNC.SUCCESS:
        return { ...state, canEnableAllCompanies: action.payload.data }

      case FETCH_SCOPE_AUTHORITY_ASYNC.ERROR:
        return { ...state, canEnableAllCompanies: false }

      case FETCH_EVENT_RULE_DETAIL_ASYNC.SUCCESS:
        if (action.payload.data.id !== state.drawer.id) return state
        return { ...state, ruleDetail: action.payload.data }

      case DELETE_EVENT_RULE_ASYNC.SUCCESS:
        return {
          ...state,
          deleteConfirm: { open: false, entityType: null, id: null },
        }

      default:
        return state
    }
  }
}
